import { Injectable } from '@angular/core';
import { interval, Subject, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { I18Service } from './i18.service';
import { NotificationService } from './notification.service';

interface AudioLevel {
  participantId: string;
  level: number;
  isSpeaking: boolean;
}

interface ParticipantAudioData {
  source: MediaStreamAudioSourceNode;
  analyser: AnalyserNode;
  dataArray: Uint8Array;
  isSpeaking: boolean;
  silenceTimer?: any;
  lastUpdateTime: number;
}

@Injectable({
  providedIn: 'root'
})
export class VoiceActivityDetectorService {
  // UN SOLO AudioContext condiviso per tutti i partecipanti
  private audioContext?: AudioContext;
  private participants = new Map<string, ParticipantAudioData>();
  
  // Observable per notificare i cambiamenti
  public audioLevels$ = new Subject<AudioLevel>();
  
  // Configurazione ottimizzata
  private readonly THRESHOLD = 20; // Soglia di volume (0-255)
  private readonly SILENCE_DELAY = 800; // ms di silenzio prima di considerare "non sta parlando"
  private readonly UPDATE_INTERVAL = 100; // ms tra ogni analisi
  private readonly MIN_UPDATE_INTERVAL = 50; // ms minimo tra update per partecipante
  
  // Subscription per il loop di analisi
  private analysisSubscription?: Subscription;
  
  constructor() {
    // this.initializeAudioContext();
  }

  /**
   * Inizializza un singolo AudioContext condiviso
   */
  private async initializeAudioContext(): Promise<void> {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
      
      // Gestisci lo stato suspended (per policies browser)
      if (this.audioContext.state === 'suspended') {
        try {
          await this.audioContext.resume();
        } catch (e: any) {
          console.warn('Errore nel resume di AudioContext:', e);
          if (e.name === 'NotAllowedError') {
            NotificationService.popupWarn({msg:I18Service.get('POPUP.CLICKFORPLAY')}).then(()=> {
            this.audioContext!.resume();
          });
        }
        }
      }
    }
  }

  /**
   * Avvia il loop di analisi ottimizzato
   */
  private startAnalysisLoop(): void {
    if (this.analysisSubscription) return;
    
    // Usa un interval con throttling per ottimizzare le performance
    this.analysisSubscription = interval(this.UPDATE_INTERVAL)
      .pipe(throttleTime(this.UPDATE_INTERVAL - 10))
      .subscribe(() => {
        this.analyzeAllParticipants();
      });
  }

  /**
   * Ferma il loop di analisi quando non ci sono partecipanti
   */
  private stopAnalysisLoop(): void {
    if (this.participants.size === 0 && this.analysisSubscription) {
      this.analysisSubscription.unsubscribe();
      this.analysisSubscription = undefined;
    }
  }

  /**
   * Analizza tutti i partecipanti in un singolo ciclo
   */
  private analyzeAllParticipants(): void {
    const now = Date.now();
    
    this.participants.forEach((data, participantId) => {
      // Throttling per partecipante per ridurre il carico
      if (now - data.lastUpdateTime < this.MIN_UPDATE_INTERVAL) return;
      
      // Ottieni i dati di frequenza
      data.analyser.getByteFrequencyData(data.dataArray);
      
      // Calcola il volume medio (ottimizzato)
      let sum = 0;
      const length = data.dataArray.length;
      // Campiona solo ogni 4 valori per velocizzare
      for (let i = 0; i < length; i += 4) {
        sum += data.dataArray[i];
      }
      const average = (sum / (length / 4));
      
      // Determina se sta parlando
      const isSpeaking = average > this.THRESHOLD;
      const wasSpeaking = data.isSpeaking;
      
      if (isSpeaking !== wasSpeaking) {
        this.handleSpeakingChange(participantId, data, isSpeaking, average);
      } else if (isSpeaking) {
        // Update continuo solo se sta parlando
        this.emitAudioLevel(participantId, average, true);
      }
      
      data.lastUpdateTime = now;
    });
  }

  /**
   * Gestisce i cambiamenti di stato speaking
   */
  private handleSpeakingChange(
    participantId: string, 
    data: ParticipantAudioData, 
    isSpeaking: boolean, 
    level: number
  ): void {
    if (isSpeaking) {
      // Inizia a parlare
      if (data.silenceTimer) {
        clearTimeout(data.silenceTimer);
        data.silenceTimer = undefined;
      }
      data.isSpeaking = true;
      this.emitAudioLevel(participantId, level, true);
    } else {
      // Potrebbe aver smesso di parlare - usa un timer per evitare falsi positivi
      if (!data.silenceTimer) {
        data.silenceTimer = setTimeout(() => {
          data.isSpeaking = false;
          this.emitAudioLevel(participantId, 0, false);
          data.silenceTimer = undefined;
        }, this.SILENCE_DELAY);
      }
    }
  }

  /**
   * Emette il livello audio con throttling
   */
  private emitAudioLevel(participantId: string, level: number, isSpeaking: boolean): void {
    this.audioLevels$.next({
      participantId,
      level: Math.round(level), // Arrotonda per ridurre updates
      isSpeaking
    });
  }

  /**
   * Inizializza il VAD per un partecipante
   * @param participantId ID univoco del partecipante
   * @param stream MediaStream del partecipante
   */
  async initializeVAD(participantId: string, stream: MediaStream): Promise<void> {
    try {
      // Assicurati che AudioContext sia inizializzato
      await this.initializeAudioContext();
      if (!this.audioContext) throw new Error('AudioContext non inizializzato');

      // Rimuovi eventuale VAD esistente per questo partecipante
      this.removeVAD(participantId);

      // Crea nodi audio
      const source = this.audioContext.createMediaStreamSource(stream);
      const analyser = this.audioContext.createAnalyser();
      
      // Configurazione ottimizzata dell'analyser
      analyser.fftSize = 256; // Più basso = meno CPU
      analyser.smoothingTimeConstant = 0.3; // Meno smoothing = più reattivo
      
      // Connetti source -> analyser
      source.connect(analyser);
      // Non connettere a destination per evitare echo!
      
      // Prepara array per i dati
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
      // Salva i dati del partecipante
      this.participants.set(participantId, {
        source,
        analyser,
        dataArray,
        isSpeaking: false,
        lastUpdateTime: 0
      });
      
      // Avvia il loop di analisi se non è già attivo
      this.startAnalysisLoop();
      
      console.log(`VAD inizializzato per partecipante ${participantId}`);
      
    } catch (error) {
      console.error('Errore nell\'inizializzazione VAD:', error);
    }
  }

  /**
   * Rimuove il VAD per un partecipante
   * @param participantId ID del partecipante
   */
  removeVAD(participantId: string): void {
    const data = this.participants.get(participantId);
    if (!data) return;

    // Pulisci timer
    if (data.silenceTimer) {
      clearTimeout(data.silenceTimer);
    }

    // Disconnetti nodi audio
    try {
      data.source.disconnect();
      data.analyser.disconnect();
    } catch (e) {
      // Ignora errori di disconnessione
    }

    // Rimuovi dalla mappa
    this.participants.delete(participantId);
    
    // Notifica che non sta più parlando
    this.emitAudioLevel(participantId, 0, false);
    
    // Ferma il loop se non ci sono più partecipanti
    this.stopAnalysisLoop();
    
    console.log(`VAD rimosso per partecipante ${participantId}`);
  }

  /**
   * Ottiene statistiche sulle performance
   */
  getPerformanceStats(): { 
    activeParticipants: number; 
    audioContextState: string;
    cpuOptimizations: string[] 
  } {
    return {
      activeParticipants: this.participants.size,
      audioContextState: this.audioContext?.state || 'not initialized',
      cpuOptimizations: [
        'Single shared AudioContext',
        'Throttled analysis loop',
        'Sampling optimization (1/4 of data)',
        'Per-participant throttling',
        'Conditional updates'
      ]
    };
  }
    /**
     * Verifica se esiste già VAD per un partecipante
     */
    hasVAD(participantId: string): boolean {
    return this.participants.has(participantId);
    }
  /**
   * Pulisce tutte le risorse
   */
  async cleanup(): Promise<void> {
    // Rimuovi tutti i partecipanti
    const participantIds = Array.from(this.participants.keys());
    participantIds.forEach(id => this.removeVAD(id));
    
    // Ferma il loop
    this.stopAnalysisLoop();
    
    // Chiudi AudioContext
    if (this.audioContext && this.audioContext.state !== 'closed') {
      await this.audioContext.close();
      this.audioContext = undefined;
    }
  }

  /**
   * Metodo per regolare la soglia dinamicamente
   * Utile per adattarsi a diversi livelli di rumore ambientale
   */
  adjustThreshold(participantId: string, noiseLevel: number): void {
    // Potresti salvare soglie personalizzate per partecipante
    // this.thresholds.set(participantId, noiseLevel + 10);
  }
}